import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useTenant } from "../lib/tenant";
import { TopBar } from "../components/TopBar";
import { api, callableErrorMessage } from "../lib/api";
import { track } from "../lib/analytics";

export function AmbassadorApply() {
  const nav = useNavigate();
  const { user, claims } = useAuth();
  const { tenant } = useTenant();
  const [name, setName] = useState(user?.displayName ?? "");
  const [instagram, setInstagram] = useState("");
  const [followers, setFollowers] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (claims.role !== "customer") {
    return (
      <>
        <TopBar />
        <main className="min-h-dvh bg-cream">
          <div className="container-page text-center">
            <p className="text-sm text-leaf-700">
              {claims.role === "ambassador"
                ? "Ya eres embajador. Ve a tu panel."
                : "Esta sección es solo para clientes."}
            </p>
            <button onClick={() => nav("/")} className="btn-primary mt-4">
              Volver
            </button>
          </div>
        </main>
      </>
    );
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    track("ambassador_apply_submitted");
    try {
      await api.submitAmbassadorApplication({ name, instagram, followers, message });
      track("ambassador_apply_success");
      setSent(true);
    } catch (err) {
      track("ambassador_apply_failed", {
        code: (err as { code?: string })?.code ?? "unknown"
      });
      setError(callableErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <>
        <TopBar />
        <main className="min-h-dvh bg-cream">
          <div className="container-page flex flex-col gap-4 text-center">
            <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-leaf-100">
              <svg viewBox="0 0 24 24" className="h-8 w-8 text-leaf-700" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12l5 5L20 7" />
              </svg>
            </div>
            <h1 className="font-display text-2xl font-semibold text-leaf-950">
              ¡Postulación enviada!
            </h1>
            <p className="text-sm text-leaf-700">
              {tenant?.name ?? "El comercio"} va a revisar tu postulación en las próximas 48 h. Te
              avisamos por email cuando esté lista.
            </p>
            <button onClick={() => nav("/")} className="btn-primary mt-2">
              Volver a mi panel
            </button>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <TopBar />
      <main className="bg-cream pb-16 pt-4">
        <div className="mx-auto flex max-w-md flex-col gap-5 px-5">
          <button onClick={() => nav("/")} className="btn-ghost self-start px-3 py-1.5 text-xs">
            ← Volver
          </button>

          <header>
            <p className="text-xs uppercase tracking-[0.2em] text-leaf-600">Programa de embajadores</p>
            <h1 className="mt-1 font-display text-3xl font-semibold text-leaf-950">
              Sé embajador de {tenant?.name ?? "este comercio"}
            </h1>
            <p className="mt-2 text-sm text-leaf-700">
              Compartes con tus seguidores, ellos compran con descuento, y tú ganas comisión por
              cada venta.
            </p>
          </header>

          <section className="grid grid-cols-2 gap-2">
            <Benefit icon="🎁" title="Producto gratis" body="Reward extra mensual." />
            <Benefit icon="💸" title="Comisión 10%" body="Por cada venta referida." />
            <Benefit icon="🔖" title="Código único" body="Solo tú lo compartes." />
            <Benefit icon="✨" title="Eventos VIP" body="Acceso prioritario." />
          </section>

          <form onSubmit={onSubmit} className="card flex flex-col gap-3">
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-leaf-700">Tu nombre</span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                minLength={2}
                className="input"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-leaf-700">Instagram</span>
              <input
                value={instagram}
                onChange={(e) => setInstagram(e.target.value)}
                required
                placeholder="@tuusuario"
                className="input"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-leaf-700">Seguidores aproximados (opcional)</span>
              <input
                value={followers}
                onChange={(e) => setFollowers(e.target.value)}
                placeholder="ej. 1200"
                className="input"
                inputMode="numeric"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-leaf-700">Mensaje al comercio (opcional)</span>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                placeholder="Cuéntales por qué encajas como embajador."
                className="input min-h-[80px]"
              />
            </label>
            {error && (
              <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}
            <button type="submit" disabled={busy} className="btn-primary">
              {busy ? "Enviando…" : "Enviar postulación"}
            </button>
          </form>
        </div>
      </main>
    </>
  );
}

function Benefit({ icon, title, body }: { icon: string; title: string; body: string }) {
  return (
    <div className="rounded-2xl bg-white p-3 ring-1 ring-leaf-100">
      <p className="text-xl">{icon}</p>
      <p className="mt-1 text-sm font-semibold text-leaf-950">{title}</p>
      <p className="text-xs text-leaf-700">{body}</p>
    </div>
  );
}
